import CreateLocationBatchFooter from '@/features/data/create-locations-batch/components/CreateLocationBatchFooter.tsx';
import CreateLocationBatchHeader from '@/features/data/create-locations-batch/components/CreateLocationBatchHeader.tsx';
import CreateLocationBatchTable from '@/features/data/create-locations-batch/components/CreateLocationBatchTable.tsx';
import useCreateLocationBatchForm from '@/features/data/create-locations-batch/hooks/useCreateLocationBatchForm.tsx';
import { Alert, AppShell } from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';
import { FormProvider } from 'react-hook-form';
import { useEffect } from 'react';
import { useSetAtom } from 'jotai';
import {
  locationBatchRowsPaginationAtom,
  locationBatchRowsStatusAtom,
} from '@/features/data/create-locations-batch/batchRowsState.ts';
import { DEFAULT_PAGINATION } from '@/common/pagination/types.ts';

export const Route = createFileRoute('/data/locations/create/$batchId')({
  component: RouteComponent,
  staticData: {
    authorization: {
      permission: {
        location: ['write'],
      },
    },
  },
});

function RouteComponent() {
  const { batchId } = Route.useParams();

  return <CreateLocationBatchPage key={batchId} batchId={batchId} />;
}

function CreateLocationBatchPage({ batchId }: { batchId: string }) {
  const setPagination = useSetAtom(locationBatchRowsPaginationAtom);
  const setStatus = useSetAtom(locationBatchRowsStatusAtom);
  const { form, summaryQuery, rowsQuery } = useCreateLocationBatchForm({
    batchId,
  });
  const error = summaryQuery.error ?? rowsQuery.error;
  const isError = summaryQuery.isError || rowsQuery.isError;

  useEffect(() => {
    setPagination(DEFAULT_PAGINATION);
    setStatus(undefined);
  }, [batchId, setPagination, setStatus]);

  return (
    <FormProvider {...form}>
      <AppShell header={{ height: 48 }} footer={{ height: 48 }} padding={0}>
        <AppShell.Header
          style={{
            borderBottom: '1px solid var(--mantine-color-default-border)',
            backgroundColor: 'var(--mantine-color-default)',
          }}
        >
          <CreateLocationBatchHeader
            batchId={batchId}
            summary={summaryQuery.data}
          />
        </AppShell.Header>
        <AppShell.Main>
          {isError && (
            <Alert
              color="red"
              variant="light"
              title="Unable to load batch"
              m="sm"
            >
              {error instanceof Error ? error.message : String(error)}
            </Alert>
          )}
          {!isError && (
            <CreateLocationBatchTable
              batchId={batchId}
              rowsData={rowsQuery.data}
              summary={summaryQuery.data}
              loading={rowsQuery.isFetched && !!rowsQuery.data}
            />
          )}
        </AppShell.Main>
        <AppShell.Footer>
          <CreateLocationBatchFooter
            batchId={batchId}
            rowsData={rowsQuery.data}
          />
        </AppShell.Footer>
      </AppShell>
    </FormProvider>
  );
}
